import { TaskType } from './Task';
import { statisticsScenarioByType } from './statisticsScenarioDefinitions';

export type ScenarioGroup =
  | 'slot'
  | 'dispensarization'
  | 'form'
  | 'informing'
  | 'unknown';
export type DirectionSupport = 'outbound' | 'inbound' | 'both';

export interface TaskScenarioMeta {
  group: ScenarioGroup;
  directionSupport: DirectionSupport;
  label?: string;
  isKnown: boolean;
}

const SLOT_TASK_TYPES: readonly TaskType[] = [
  'OutboundConfirmVisit',
  'OutboundCancelVisit',
  'OutboundVisitRegister',
  'InboundVisitRegister',
];

const DISPENSARIZATION_TASK_TYPES: readonly TaskType[] = [
  'OutboundInformingDispensarization',
];

const INFORMING_TASK_TYPES: readonly TaskType[] = [
  'OutboundPositiveTickTest',
  'GeneralInformation',
  'Hotline',
];

const FORM_TASK_TYPES: readonly TaskType[] = [
  'Survey',
  'QualityEstimation',
  'MonitoringDiabetesMellitus',
  'MonitoringElderlyPatient',
  'MonitoringHepatitis',
  'MonitoringTuberculosis',
  'MonitoringOncological',
  'MonitoringChronicObstructivePulmonaryDisease',
  'MonitoringHeartFailure',
  'MonitoringCoronaryHeartDisease',
  'MonitoringAsthma',
  'MonitoringArterialHypertension',
  'MonitoringSarsCovid19',
  'MonitoringDispensary',
  'Hospitalization',
];

function resolveGroup(taskType: string): ScenarioGroup {
  if ((SLOT_TASK_TYPES as readonly string[]).includes(taskType)) {
    return 'slot';
  }
  if ((DISPENSARIZATION_TASK_TYPES as readonly string[]).includes(taskType)) {
    return 'dispensarization';
  }
  if ((FORM_TASK_TYPES as readonly string[]).includes(taskType)) {
    return 'form';
  }
  if ((INFORMING_TASK_TYPES as readonly string[]).includes(taskType)) {
    return 'informing';
  }
  return 'unknown';
}

function resolveDirectionSupport(taskType: string): DirectionSupport {
  if (taskType.startsWith('Outbound')) {
    return 'outbound';
  }
  if (taskType.startsWith('Inbound')) {
    return 'inbound';
  }
  return 'both';
}

export function getTaskScenarioMeta(
  taskType: string,
  backendLabel?: string | null,
): TaskScenarioMeta {
  const group = resolveGroup(taskType);
  const directionSupport = resolveDirectionSupport(taskType);
  const isKnown = group !== 'unknown';

  const label = backendLabel ?? undefined;

  return {
    group,
    directionSupport,
    label,
    isKnown,
  };
}

// ---------------------------------------------------------------------------
// Statistics-facing metadata layer (can replace scenarioMetadata.ts)
// ---------------------------------------------------------------------------

export type ScenarioVariant =
  | 'dispensarization'
  | 'slot'
  | 'appointment'
  | 'informing'
  | 'questionnaire'
  | 'default';

export type ScenarioGroupKey =
  | 'dispensarization'
  | 'slotTasks'
  | 'appointmentRegistration'
  | 'informing'
  | 'questionnaires'
  | 'other';

export interface StatisticsScenarioMetadata {
  groupKey: ScenarioGroupKey;
  groupLabel: string;
  optionLabel: string;
  showCampaign: boolean;
  showQuestionnaireTemplate: boolean;
  variant: ScenarioVariant;
  directionSupport: DirectionSupport;
  isKnown: boolean;
}

function buildFallbackStatisticsMetadata(
  type: string,
  backendLabel?: string | null,
): StatisticsScenarioMetadata {
  const directionSupport = resolveDirectionSupport(type);

  return {
    groupKey: 'other',
    groupLabel: 'Прочее',
    optionLabel: backendLabel ?? type,
    showCampaign: false,
    showQuestionnaireTemplate: false,
    variant: 'default',
    directionSupport,
    isKnown: false,
  };
}

export function getStatisticsScenarioMetadata(
  type: string,
  backendLabel?: string | null,
): StatisticsScenarioMetadata {
  const metadata = statisticsScenarioByType.get(type);

  if (metadata) {
    // Return a shallow copy without the internal `type` field
    const { type: _type, ...rest } = metadata;
    return rest;
  }

  return buildFallbackStatisticsMetadata(type, backendLabel);
}

export function getSelectedScenarioVariant(
  type: string | null,
): ScenarioVariant {
  if (!type) {
    return 'default';
  }

  return getStatisticsScenarioMetadata(type).variant;
}

export function shouldShowCampaign(type: string | null): boolean {
  if (!type) {
    return false;
  }

  return getStatisticsScenarioMetadata(type).showCampaign;
}

export function shouldShowQuestionnaireTemplate(type: string | null): boolean {
  if (!type) {
    return false;
  }

  return getStatisticsScenarioMetadata(type).showQuestionnaireTemplate;
}

interface TaskTypeOptionDictionaryItem {
  key: string | null;
  value: string | null;
}

export function buildTaskTypeOptions(
  dictionary: readonly TaskTypeOptionDictionaryItem[],
): Array<{ value: string; label: string }> {
  return dictionary
    .filter((item): item is { key: string; value: string | null } =>
      Boolean(item.key),
    )
    .map((item) => {
      const meta = getStatisticsScenarioMetadata(item.key, item.value);
      return {
        value: item.key,
        label: item.value ?? meta.optionLabel,
      };
    });
}
